import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Text, Button, ProgressBar, Badge, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircle20Filled, Circle20Regular, CloudAdd20Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router';
import { apiClient } from '../api/client';
import { useT } from '../i18n';
import { usePermission } from '../hooks/usePermission';

const useStyles = makeStyles({
  root: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge, marginBottom: tokens.spacingVerticalL,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  step: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, marginTop: tokens.spacingVerticalS },
  stepText: { display: 'flex', flexDirection: 'column', flexGrow: 1 },
  bulkImportBanner: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    padding: tokens.spacingHorizontalM, backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium, marginTop: tokens.spacingVerticalXS,
  },
});

type Block = 'structure' | 'people' | 'operation' | 'finance' | 'system';
interface OnboardingItem {
  id: string;
  block: Block;
  done: boolean;
  required: boolean;
  manual?: boolean;
  detail?: string;
}

interface OnboardingStatus {
  steps: { potreros: boolean; cultivos: boolean; insumos: boolean };
  items?: OnboardingItem[];
}

const BLOCK_ORDER: Block[] = ['structure', 'people', 'operation', 'finance', 'system'];

// Rutas reales de App.tsx. No existe /configuracion: ajustes de sistema/tenant viven en super-admin/config.
const ITEM_PATHS: Record<string, string> = {
  rancho: 'ranchos',
  lotes_geocerca: 'mapa',
  cultivo: 'cultivo',
  usuarios_accesos: 'usuarios',
  telegram: 'usuarios',
  grupo_telegram: 'ranchos',
  productos_reglas: 'inventario',
  bodega_stock: 'inventario',
  maquinaria: 'maquinaria',
  nomina: 'nomina',
  presupuesto: 'finanzas',
  locale: 'super-admin/config',
  proveedores_ia: 'super-admin/config',
  reportes: 'reportes',
};

/** Backend viejo sin `items`: se reconstruyen los 3 pasos originales (todos requeridos). */
function fallbackItems(steps: OnboardingStatus['steps']): OnboardingItem[] {
  return [
    { id: 'lotes_geocerca', block: 'structure', done: !!steps?.potreros, required: true },
    { id: 'cultivo', block: 'structure', done: !!steps?.cultivos, required: true },
    { id: 'bodega_stock', block: 'operation', done: !!steps?.insumos, required: true },
  ];
}

export default function OnboardingChecklist({ tenantId }: { tenantId: string }) {
  const s = useStyles();
  const navigate = useNavigate();
  const t = useT();
  const queryClient = useQueryClient();
  const canBulkImport = usePermission('admin:bulk-import');

  const { data } = useQuery<OnboardingStatus>({
    queryKey: ['onboardingStatus', tenantId],
    queryFn: async () => (await apiClient.get(`/api/onboarding/status`)).data,
  });

  const review = useMutation({
    mutationFn: async (itemId: string) => (await apiClient.post(`/api/onboarding/review`, { itemId })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboardingStatus', tenantId] }),
  });

  if (!data) return null;

  const items = data.items ?? fallbackItems(data.steps);
  const requiredItems = items.filter((i) => i.required);
  const optionalItems = items.filter((i) => !i.required);
  const completedCount = requiredItems.filter((i) => i.done).length;
  const total = requiredItems.length;
  const isComplete = completedCount === total;

  const itemTexts = t.onboarding.items as Record<string, { label: string; desc: string }>;
  const blockTitles = t.onboarding.blocks as Record<string, string>;

  const renderItem = (item: OnboardingItem) => {
    const texts = itemTexts[item.id];
    const path = ITEM_PATHS[item.id];
    return (
      <div key={item.id} className={s.step} data-testid={`onboarding-item-${item.id}`}>
        {item.done ? <CheckmarkCircle20Filled /> : <Circle20Regular />}
        <div className={s.stepText}>
          <Text>{texts?.label ?? item.id}</Text>
          {texts?.desc && <Text size={200}>{texts.desc}</Text>}
          {item.detail && <Text size={200} italic>{item.detail}</Text>}
        </div>
        <Badge appearance="outline" color={item.required ? 'danger' : 'informative'}>
          {item.required ? t.onboarding.required : t.onboarding.optional}
        </Badge>
        {item.manual && !item.done && (
          <Button appearance="outline" size="small" disabled={review.isPending} onClick={() => review.mutate(item.id)}>
            {t.onboarding.markReviewed}
          </Button>
        )}
        {path && (
          <Button appearance="subtle" onClick={() => navigate(`/${tenantId}/${path}`)}>
            {t.onboarding.go}
          </Button>
        )}
      </div>
    );
  };

  const blocks = BLOCK_ORDER.map((b) => ({ block: b, items: requiredItems.filter((i) => i.block === b) })).filter(
    (g) => g.items.length > 0,
  );
  const optionalBlocks = BLOCK_ORDER.map((b) => ({ block: b, items: optionalItems.filter((i) => i.block === b) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className={s.root}>
      <div className={s.header}>
        <Text weight="semibold">{isComplete ? t.onboarding.celebration : t.onboarding.title}</Text>
        <Text>
          {completedCount}/{total}
        </Text>
      </div>
      <ProgressBar value={total ? completedCount / total : 1} />
      {blocks.map((g) => (
        <div key={g.block} className={s.block}>
          <Text weight="semibold">{blockTitles[g.block]}</Text>
          {g.items.map(renderItem)}
        </div>
      ))}
      {optionalBlocks.length > 0 && (
        <div className={s.block}>
          <Text weight="semibold">{t.onboarding.optionalTitle}</Text>
          {optionalBlocks.map((g) => (
            <div key={g.block} className={s.block}>
              <Text size={200}>{blockTitles[g.block]}</Text>
              {g.items.map(renderItem)}
            </div>
          ))}
        </div>
      )}
      {canBulkImport && !isComplete && (
        <div className={s.bulkImportBanner}>
          <CloudAdd20Regular />
          <Text size={200} style={{ flexGrow: 1 }}>{t.onboarding.bulkImportHint}</Text>
          <Button appearance="outline" size="small" onClick={() => navigate(`/${tenantId}/bulk-import`)}>
            {t.onboarding.bulkImportCta}
          </Button>
        </div>
      )}
    </div>
  );
}
